import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, ShieldCheck, Heart, Target } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const VALUES = [
  { icon: Heart, title: "Hospitality First", desc: "Every stay treated like we're hosting family." },
  { icon: ShieldCheck, title: "Radical Transparency", desc: "Clear statements, honest pricing, no surprises." },
  { icon: Target, title: "Owner Success", desc: "Your profitability is the metric we live by." },
  { icon: Award, title: "Relentless Quality", desc: "Inspected, photo-verified, and always improving." },
];

export default function About() {
  const [team, setTeam] = useState([]);
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data.team));
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  return (
    <div>
      <div className="relative overflow-hidden bg-ink py-24 text-white">
        <img src="https://customer-assets-v7afamib.emergentagent.net/job_elite-booking-10/artifacts/ccx29jan_f68d0e5a-7cde-4064-9ab5-522a1b4c430c.png" alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="relative mx-auto max-w-3xl px-5 text-center lg:px-8">
          <span className="overline text-gold">About Us</span>
          <h1 className="mt-4 text-4xl lg:text-6xl">Hospitality, engineered.</h1>
          <p className="mt-4 text-lg text-white/70">Elite STR Hosting Co. blends five-star service with technology to deliver unforgettable stays and market-leading returns.</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-5 py-16 lg:px-8">
        <span className="overline">Our story</span>
        <h2 className="mt-2 text-3xl lg:text-4xl">Founded by a host who wanted better.</h2>
        <p className="mt-4 text-lg leading-relaxed text-ink/80">
          After managing her own Hill Country homes and seeing guests and owners underserved by cookie-cutter management companies, our founder built Elite STR around a simple idea: treat every property like a business and every guest like a VIP. Today we manage curated luxury rentals across Texas, powered by dynamic pricing, a 24/7 concierge, and an obsessive attention to detail.
        </p>
      </div>

      {stats && (
        <div className="bg-cream py-14">
          <div className="mx-auto grid max-w-5xl grid-cols-2 gap-8 px-5 lg:grid-cols-4 lg:px-8">
            {[
              { v: `${(stats.happy_guests / 1000).toFixed(0)}k+`, l: "Happy guests" },
              { v: `$${stats.total_bookings_usd}M+`, l: "In bookings" },
              { v: `${stats.avg_occupancy}%`, l: "Avg occupancy" },
              { v: `+${stats.avg_revenue_increase}%`, l: "Revenue lift" },
            ].map((s, i) => (
              <div key={i} className="text-center"><div className="text-4xl font-medium text-ink">{s.v}</div><div className="overline mt-1">{s.l}</div></div>
            ))}
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <h2 className="text-3xl lg:text-4xl">Our values</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <div key={i} className="rounded-3xl border border-black/5 bg-white p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-cream"><v.icon size={20} strokeWidth={1.5} /></span>
              <h3 className="mt-4 text-xl">{v.title}</h3>
              <p className="mt-2 text-sm text-taupe">{v.desc}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-16 text-3xl lg:text-4xl">Meet our team</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {team.map((m) => (
            <div key={m.name} className="rounded-3xl card-lux p-6 text-center">
              <img src={m.avatar} alt={m.name} className="mx-auto h-24 w-24 rounded-full object-cover" />
              <h3 className="mt-4 text-xl text-ink">{m.name}</h3>
              <div className="overline mt-1">{m.role}</div>
              <p className="mt-3 text-sm text-taupe">{m.bio}</p>
            </div>
          ))}
        </div>

        <div className="mt-16 rounded-3xl bg-ink p-10 text-center text-white md:p-14">
          <h2 className="text-3xl lg:text-4xl">Let's work together</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/rentals"><Button className="rounded-full bg-white px-6 text-ink hover:bg-camel hover:text-white">Book a stay</Button></Link>
            <Link to="/owner-services"><Button variant="outline" className="rounded-full border-white/30 bg-transparent px-6 text-white hover:bg-white hover:text-ink">List your property</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
